import { useEffect, useRef, useState } from "react";
import { Loader2, PlaneTakeoff } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export const AirportField = ({ label, value, placeholder, onPick, testId }) => {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const wrap = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    if (!open || !q.trim()) { setResults([]); return undefined; }
    let live = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/airports", { params: { q: q.trim() } }); if (live) { setResults(data.results); setActive(0); } } catch { if (live) setResults([]); }
      if (live) setLoading(false);
    }, 180);
    return () => { live = false; clearTimeout(t); };
  }, [q, open]);
  useEffect(() => {
    const close = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (a) => { const text = `${a.city || a.name} (${a.code})`; setQ(text); setOpen(false); onPick(text, `${a.lat},${a.lon}`); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && open && results[active]) { e.preventDefault(); pick(results[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={wrap} className="relative">
      <label className="block"><span className="field-label">{label}</span>
        <div className="relative">
          <input className="field pr-9" role="combobox" aria-expanded={open} autoComplete="off" maxLength={60} value={q} placeholder={placeholder}
            onChange={(e) => { setQ(e.target.value); setOpen(true); onPick(e.target.value, ""); }} onFocus={() => setOpen(true)} onKeyDown={onKey} data-testid={testId} />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlaneTakeoff className="h-4 w-4" />}</span>
        </div>
      </label>
      {open && results.length > 0 && (
        <ul role="listbox" className="absolute z-30 mt-2 max-h-64 w-full min-w-[260px] overflow-y-auto rounded-lg border border-border bg-card py-1 shadow-xl" data-testid={`${testId}-listbox`}>
          {results.map((a, i) => (
            <li key={a.code} role="option" aria-selected={i === active} onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); pick(a); }}
              className={cn("flex cursor-pointer items-baseline gap-3 px-4 py-2.5 text-sm", i === active ? "bg-secondary" : "text-foreground/85")} data-testid={`${testId}-option-${i}`}>
              <span className="w-10 shrink-0 font-semibold text-primary">{a.code}</span>
              <span className="min-w-0 truncate">{a.name}<span className="text-muted-foreground"> · {a.city ? `${a.city}, ` : ""}{a.country}</span></span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
